
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Facebook, Instagram, Youtube, Twitter, Link, Users, Trash2 } from 'lucide-react';

const platformIcons = {
  facebook: Facebook,
  instagram: Instagram,
  youtube: Youtube,
  twitter: Twitter,
  tiktok: Link
};

const platformColors = {
  facebook: 'bg-blue-600',
  instagram: 'bg-pink-600',
  youtube: 'bg-red-600',
  twitter: 'bg-sky-500',
  tiktok: 'bg-black'
};

interface ConnectedSocialAccountProps {
  account: {
    id: string;
    platform: string;
    account_name?: string;
    account_username?: string;
  };
  facebookGroups?: Array<{
    id: string;
    group_name: string;
    group_privacy: string;
  }>;
  groupsLoading: boolean;
  onRemoveAccount: (accountId: string) => void;
}

export const ConnectedSocialAccount = ({ 
  account, 
  facebookGroups = [], 
  groupsLoading, 
  onRemoveAccount 
}: ConnectedSocialAccountProps) => {
  const IconComponent = platformIcons[account.platform as keyof typeof platformIcons];
  const colorClass = platformColors[account.platform as keyof typeof platformColors];

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between p-3 border rounded-lg">
        <div className="flex items-center space-x-3">
          <div className={`p-2 rounded-full ${colorClass} text-white`}>
            <IconComponent size={16} />
          </div>
          <div>
            <div className="font-medium capitalize">{account.platform}</div>
            <div className="text-sm text-gray-600">
              {account.account_name || account.account_username || 'Connected'}
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <Badge variant="secondary" className="bg-green-100 text-green-800">
            Connected
          </Badge>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onRemoveAccount(account.id)}
            className="text-red-600 hover:text-red-700 hover:bg-red-50"
          >
            <Trash2 size={14} />
          </Button>
        </div>
      </div>

      {/* Show Facebook Groups if this is a Facebook account */}
      {account.platform === 'facebook' && (
        <div className="ml-8 p-3 bg-blue-50 rounded-lg">
          <div className="flex items-center space-x-2 mb-2">
            <Users size={16} className="text-blue-600" />
            <span className="text-sm font-medium text-blue-800">Facebook Groups</span>
          </div>
          {groupsLoading ? (
            <p className="text-xs text-blue-600">Loading groups...</p>
          ) : facebookGroups.length > 0 ? (
            <div className="space-y-1">
              {facebookGroups.slice(0, 3).map((group) => (
                <div key={group.id} className="text-xs text-blue-700">
                  • {group.group_name} ({group.group_privacy})
                </div>
              ))}
              {facebookGroups.length > 3 && (
                <div className="text-xs text-blue-600">
                  +{facebookGroups.length - 3} more groups
                </div>
              )}
            </div>
          ) : (
            <p className="text-xs text-blue-600">No groups available for posting</p>
          )}
        </div>
      )}
    </div>
  );
};
